import { useFeatureFlags } from "@/composables/use-feature-flags";
import { DateTime } from "luxon";

export interface LoggerOptions {
    isEnabled: boolean,
    logLevel: string,
    showLogLevel: boolean,
    showTimestamp: boolean,
    showMethodName: boolean,
    separator: string,
    showConsoleColors: boolean
}

const { isProduction } = useFeatureFlags();

export function getDefaultLoggerOptions( overrideLevel: LogLevels = LogLevels.ERROR ): LoggerOptions {
    return {
        isEnabled: !isProduction.value,
        logLevel: isProduction.value ? LogLevels.ERROR : overrideLevel,
        separator: "|",
        showConsoleColors: true,
        showLogLevel: false,
        showTimestamp: true,
        showMethodName: true
    };
}

export interface ILogger {
    debug: ( msg: string, ...args: any[] ) => void;
    info: ( msg: string, ...args: any[] ) => void;
    log: ( msg: string, ...args: any[] ) => void;
    warn: ( msg: string, ...args: any[] ) => void;
    error: ( msg: string, ...args: any[] ) => void;
}

const debugStyle = 'background-color: darkblue; color: white; font-style: bold; border: 1px solid grey; font-size: 1em;';
const infoStyle = 'background-color: purple; color: white; font-style: bold; border: 1px solid grey; font-size: 1em;';
const logStyle = 'background-color: yellow; color: darkgreen; font-style: bold; border: 1px solid grey; font-size: 1em;';
const warnStyle = 'background-color: transparent; color: yellow; font-style: bold';
const errorStyle = 'background-color: transparent; color: white; font-style: bold';

class Logger implements ILogger {
    debug(): void {}
    info(): void {}
    log(): void {}
    warn(): void {}
    error(): void {}
}

class ConsolePrinter {
    readonly #console: Console;
    readonly #showColor: boolean;

    constructor( console: Console, useColors: boolean ) {
        this.#console = console;
        this.#showColor = useColors;
    }

    debug( msg: string, args?: any[] | undefined ): void {
        const hasArgs = typeof args !== 'undefined' && args.length > 0;
        if ( this.#showColor ) {
            hasArgs ? this.#console.debug( `%c${ msg }`, debugStyle, ...args ) :
                this.#console.debug( `%c${ msg }`, debugStyle );
        } else {
            hasArgs ? this.#console.debug( msg, ...args ) :
                this.#console.debug( msg );
        }
    }

    info( msg: string, args?: any[] | undefined ): void {
        const hasArgs = typeof args !== 'undefined' && args.length > 0;
        if ( this.#showColor ) {
            hasArgs ? this.#console.info( `%c${ msg }`, infoStyle, ...args ) :
                this.#console.info( `%c${ msg }`, infoStyle );
        } else {
            hasArgs ? this.#console.info( msg, ...args ) :
                this.#console.info( msg );
        }
    }

    log( msg: string, args?: any[] | undefined ): void {
        const hasArgs = typeof args !== 'undefined' && args.length > 0;
        if ( this.#showColor ) {
            hasArgs ? this.#console.log( `%c${ msg }`, logStyle, ...args ) :
                this.#console.log( `%c${ msg }`, logStyle );
        } else {
            hasArgs ? this.#console.log( msg, ...args ) :
                this.#console.log( msg );
        }
    }

    warn( msg: string, args?: any[] | undefined ): void {
        const hasArgs = typeof args !== 'undefined' && args.length > 0;
        if ( this.#showColor ) {
            hasArgs ? this.#console.warn( `%c${ msg }`, warnStyle, ...args ) :
                this.#console.warn( `%c${ msg }`, warnStyle );
        } else {
            hasArgs ? this.#console.warn( msg, ...args ) :
                this.#console.warn( msg );
        }
    }

    error( msg: string, args?: any[] | undefined ): void {
        const hasArgs = typeof args !== 'undefined' && args.length > 0;
        if ( this.#showColor ) {
            hasArgs ? this.#console.error( `%c${ msg }`, errorStyle, ...args ) :
                this.#console.error( `%c${ msg }`, errorStyle );
        } else {
            hasArgs ? this.#console.error( msg, ...args ) :
                this.#console.error( msg );
        }
    }
}

export enum LogLevels {
    DEBUG = 'debug',
    INFO = 'info',
    LOG = 'log',
    WARN = 'warn',
    ERROR = 'error'
}

const CONFIGURATION_ERROR_MESSAGE: string = "The provided options for logger are not valid.";
const LOG_LEVELS: string[] = Object.keys( LogLevels ).map( ( l ) => l.toLowerCase() );

export interface ILoggerFactory {
    create( options: LoggerOptions ): ILogger;
}

class ConsoleLoggerFactory implements ILoggerFactory {
    public create( options: LoggerOptions ): ILogger {
        if ( this.isValidOptions( options, LOG_LEVELS ) ) {
            return this.initLoggerInstance( options );
        } else {
            throw new Error( CONFIGURATION_ERROR_MESSAGE );
        }
    }

    private getMethodName(): string {
        let methodName: string = '';
        try {
            const tracer: Error = new Error();
            let stackTrace: string = tracer?.stack?.split( "\n" )[ 3 ] || '';
            if ( / /.test( stackTrace ) ) {
                stackTrace = stackTrace.trim().split( " " )[ 1 ];
            }

            methodName = stackTrace;
        } catch ( _ ) {
            // Swallow the error, not important
        }

        return methodName.trim();
    }

    private readonly nonCriticalLoggingLevels: LogLevels[] = [ LogLevels.DEBUG, LogLevels.INFO, LogLevels.LOG, LogLevels.WARN ];

    private initLoggerInstance( options: LoggerOptions ): ILogger {
        const logger: any = new Logger();
        const printer = new ConsolePrinter( console, options.showConsoleColors );

        if ( !isProduction.value ) {
            LOG_LEVELS.forEach( ( logLevel: string ): void => {
                // The following automatically disables LogLevels (DEBUG | INFO | LOG | WARN) when run under a production build
                const level: LogLevels = LogLevels[ logLevel as keyof typeof LogLevels ];
                const enable: boolean = !this.nonCriticalLoggingLevels.includes( level ) || options.isEnabled;

                if ( LOG_LEVELS.indexOf( logLevel ) >= LOG_LEVELS.indexOf( options.logLevel ) && enable ) {
                    logger[ logLevel ] = ( msg: string, args?: any[] ): void => {
                        const methodNamePrefix: string = options.showMethodName ? `${ this.getMethodName() } ${ options.separator } ` : '';
                        const logLevelPrefix: string = options.showLogLevel ? `${ options.separator } ${ logLevel.toUpperCase() } ${ options.separator } ` : '';
                        const timestampPrefix: string = options.showTimestamp ? `${ DateTime.now().toLocaleString( DateTime.DATETIME_SHORT_WITH_SECONDS ) } ${ options.separator } ` : '';
                        const logMessage: string = `${ logLevelPrefix }${ timestampPrefix }${ methodNamePrefix }${ msg }`;

                        switch ( logLevel ) {
                            case LogLevels.DEBUG:
                                printer.debug( logMessage, args );
                                break;
                            case LogLevels.INFO:
                                printer.info( logMessage, args );
                                break;
                            case LogLevels.LOG:
                                printer.log( logMessage, args );
                                break;
                            case LogLevels.WARN:
                                printer.warn( logMessage, args );
                                break;
                            case LogLevels.ERROR:
                                printer.error( logMessage, args );
                                break;
                            default:
                                printer.info( logMessage, args );
                        }
                    };
                } else {
                    logger[ logLevel ] = () => undefined;
                }
            } );
        }

        return logger;
    }

    private isValidOptions( options: LoggerOptions, logLevels: string[] ): boolean {
        return !!options.logLevel && logLevels.indexOf( options.logLevel ) > -1;
    }
}

const consoleLoggerFactory: ILoggerFactory = new ConsoleLoggerFactory();
export { consoleLoggerFactory };
